import { useEffect, useRef, useState } from "react";

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, ...options }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, isInView];
};

const Section = ({ children, className, id }) => {
  const [ref, isInView] = useInView();
  return (
    <section
      ref={ref}
      id={id}
      className={`section ${className || ""} ${isInView ? "in-view" : ""}`}
    >
      {children}
    </section>
  );
};

const experiences = [
  {
    company: "Salesforce",
    location: "San Francisco, California",
    roles: [
      {
        title: "Senior Full-Stack Software Engineer",
        period: "June 2025 – Present",
        highlights: [
          "Engineered and deployed custom full-stack components on the Salesforce platform to streamline critical Benefit Management processes for public sector clients",
          "Created brand new prompt templates for AI agents to be able to more effectively assist Benefit Management caseworkers",
          "Mentored junior engineers and new team members, accelerating their ramp-up and fostering a collaborative, high-performing environment",
          "Spearheaded org-wide innovation initiatives, leading multi-person teams to host presentations and discussions that promoted knowledge-sharing and strategic growth",
        ],
      },
    ],
  },
  {
    company: "Flagship",
    location: "San Francisco, California",
    roles: [
      {
        title: "Full-Stack Software Engineer",
        period: "April 2024 – June 2025",
        highlights: [
          "Created a new product that uses AI and LLMs to allow brands to more easily search for and connect with content creators, used by companies across multiple industries",
          "Integrated AI-generated talking-points for content creators to more easily advertise brands to their followers",
          "Led a team of engineers and designers in owning an app that connects content creators with brands to sell the brands' products, resulting in a 20% increase in weekly active users",
          "Led technical development for features in the NextJS TypeScript app, using React and MUI for the frontend and using Prisma to interact with our Postgres database",
          "Created datasets and analytics dashboards using Sigma and SQL to determine which growth areas to prioritize in order to continue to scale our user base of hundreds of creators nationwide",
          "Used Terraform to pipe data from our Postgres database to Google BigQuery",
          "Helped develop an iOS app using Swift and WebKit to display our web app to creators, which was downloaded and used by 30-40% of our creators daily",
          "Created an exponential backoff system to avoid spamming users with notifications",
          "Designed and implemented features to increase creator sharing, which ended up increasing daily sharing rates by over 5x",
          "Designed and implemented nominators and rankers to properly display relevant deals to creators",
          "Designed and implemented a tracking pixel system to monitor open rates for email notifications",
          "Created new frontend components for displaying shared images and video to the user",
        ],
      },
    ],
  },
  {
    company: "Google",
    division: "ChromeOS",
    location: "Mountain View, California",
    roles: [
      {
        title: "Software Engineer III",
        period: "November 2022 – April 2024",
        highlights: [
          "Created new virtual desk navigation features within ChromeOS in C++. These navigation features significantly improved the efficiency and visibility of virtual desks for tens of thousands of ChromeOS users daily",
          "Gave two internal tech talks on accessing Chrome's usage metrics storage system using Google's SQL framework and creating unit tests using Chromium's gTest framework",
          "Led a collaborative multi-team effort to create and expand pipelines for ChromeOS services to receive data from the Google Tasks third-party API",
        ],
      },
      {
        title: "Software Engineer II",
        period: "September 2021 – October 2022",
        highlights: [
          "Led the development of a feature for closing a Virtual Desk with its windows in C++. This feature was used by tens of thousands of ChromeOS users daily",
          "Implemented pipelines in a Wayland server to maintain the z-ordering of PIP windows within the compositor when displaying videos from the Chrome browser",
        ],
      },
    ],
  },
  {
    company: "Illumis",
    location: "New York, New York",
    roles: [
      {
        title: "Frontend Development Intern",
        period: "June 2021 – August 2021",
        highlights: [
          "Designed and created an internal-facing user interface to visualize the metadata and structure of a data warehouse using React and Material-UI in an Agile development environment",
          "Assisted in the development of a FastAPI backend to serve metadata to the frontend",
          "Deployed containerized applications to Google Cloud using Docker and Kubernetes",
        ],
      },
    ],
  },
  {
    company: "Center for Political Accountability",
    location: "Washington, D.C.",
    roles: [
      {
        title: "Werner Brandt Research Fellow",
        period: "June 2020 – May 2021",
        highlights: [
          "Directed, compiled, and analyzed research on donations to IRS Section 527 non-profit organizations",
          "Created Excel macros to streamline the processing of donation records into a list of aggregate contributions from individual donors over a specific period of time",
          "Performed statistical analyses of the data collected",
          "Created, trained, and used a machine learning model to more quickly label donations data",
        ],
      },
      {
        title: "Research Intern",
        period: "February 2019 – June 2020",
        highlights: [
          "Designed and implemented an efficient categorization algorithm in Python to expedite research processes using sockets to link the program to a central data repository",
          "Led group of interns to continue research on donations to 527 non-profit organizations",
        ],
      },
    ],
  },
];

const projects = [
  {
    name: "Focus Mode",
    context: "Project Member @ Google",
    period: "September 2023 – April 2024",
    description: [
      "A feature in ChromeOS that allows users to set a Do Not Disturb mode timer and work on a particular task",
      "Interacts with the Google Tasks third-party API to synchronize user task data across multiple devices",
    ],
    tech: ["C++", "ChromeOS", "Google Tasks API"],
  },
  {
    name: "Desk Button",
    context: "Project Member @ Google",
    period: "March 2023 – September 2023",
    description: [
      "A feature in ChromeOS that allows users to switch between Virtual Desks and view their virtual desks without having to enter Overview Mode",
      "Involved an extensive refactor of Virtual Desk and Overview Mode code to allow the UI elements to exist independent of one another",
    ],
    tech: ["C++", "ChromeOS", "UI Development"],
  },
  {
    name: "Close-All",
    context: "Project Lead @ Google",
    period: "January 2022 – August 2022",
    description: [
      "A feature in ChromeOS that allows users to close a desk and all of the windows in the desk",
      "Provides users the option to undo the operation if they performed the action by accident",
      "Temporarily stores closed windows in local storage for efficient retrieval if the user undoes the operation",
    ],
    tech: ["C++", "ChromeOS", "Local Storage"],
  },
  {
    name: "Meta",
    context: "Project Lead @ Illumis",
    period: "June 2021 – August 2021",
    description: [
      "Internal tool coded in React and Material-UI for visualizing metadata and structure of a data warehouse",
      "Uses NGINX for load-balancing and was deployed to Google Cloud using Docker and Kubernetes",
      "Utilizes APIs created with FastAPI to serve data to the frontend",
    ],
    tech: ["React", "Material-UI", "FastAPI", "Docker", "Kubernetes", "Google Cloud"],
  },
  {
    name: "The JacksaFactions Minecraft Server Plugin",
    context: "Project Lead @ JacksaTV Minecraft Server",
    period: "March 2021 – April 2021",
    description: [
      "Uses Spigot and Bungee APIs to create a faction system for Minecraft servers",
      "Interacts with a MySQL database to maintain data consistency across multiple servers on a network",
      "Implements the Java Multithreading and Runnable libraries and caching to curb server lag and increase scalability",
    ],
    tech: ["Java", "Spigot API", "Bungee API", "MySQL", "Multithreading"],
  },
  {
    name: "The CPA Research Utility",
    context: "Project Lead @ Center for Political Accountability",
    period: "March 2019 – January 2021",
    description: [
      "A Python program that takes in categorized datasets and then uses those category labels to fill in categories for uncategorized datasets",
      "Utilizes Python dictionaries and a central data repository to store category data for later use",
      "Implements an intuitive command line user interface to maximize accessibility for users",
    ],
    tech: ["Python", "CLI Development", "Data Processing"],
  },
];

const skillCategories = [
  {
    label: "AI & Tools",
    skills: ["GPT", "Gemini", "Cursor", "Prompt Engineering"],
  },
  {
    label: "Frontend",
    skills: ["React", "Next.js", "TypeScript", "Tailwind CSS", "HTML / CSS / JS", "UI Development"],
  },
  {
    label: "Backend & Systems",
    skills: ["Node.js", "API Design", "FastAPI", "Java", "C++", "Python", "Operating Systems"],
  },
  {
    label: "Data",
    skills: ["SQL", "Data Analytics"],
  },
  {
    label: "Infrastructure",
    skills: ["Docker", "Kubernetes", "Google Cloud", "Terraform"],
  },
];

const Home = () => {
  return (
    <main>
      {/* Hero */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-name">
            <span>Benjamin</span>
            <span>Becker</span>
          </h1>
          <div className="hero-line" />
          <p className="hero-title">Senior Software Engineer at Salesforce</p>
          <p className="hero-subtitle">
            bckrben7@gmail.com &middot; 610-715-7578
          </p>
        </div>
        <div className="hero-scroll">
          <span>Scroll</span>
          <div className="hero-scroll-line" />
        </div>
      </section>

      {/* Experience */}
      <Section id="experience">
        <span className="section-label">01 — Experience</span>
        <h2 className="section-title">Where I've worked</h2>
        <div className="experience-list">
          {experiences.map((exp) => (
            <div className="experience-card" key={exp.company}>
              <div className="experience-meta">
                <span className="experience-location">{exp.location}</span>
              </div>
              <div className="experience-body">
                <h3 className="experience-company">
                  {exp.company}
                  {exp.division && (
                    <span className="division"> ({exp.division})</span>
                  )}
                </h3>
                {exp.roles.map((role, ri) => (
                  <div className="experience-role-block" key={ri}>
                    <div className="experience-role-header">
                      <span className="experience-role">{role.title}</span>
                      <span className="experience-period">{role.period}</span>
                    </div>
                    <ul className="experience-highlights">
                      {role.highlights.map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Projects */}
      <Section id="projects">
        <span className="section-label">02 — Projects</span>
        <h2 className="section-title">What I've built</h2>
        <div className="projects-grid">
          {projects.map((project) => (
            <div className="project-card" key={project.name}>
              <div className="project-header">
                <h3 className="project-name">{project.name}</h3>
                <span className="project-context">{project.context}</span>
                <span className="project-period">{project.period}</span>
              </div>
              <ul className="project-description">
                {project.description.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
              <div className="project-tech">
                {project.tech.map((t) => (
                  <span className="tech-tag" key={t}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Skills */}
      <Section id="skills">
        <span className="section-label">03 — Skills</span>
        <h2 className="section-title">What I work with</h2>
        <div className="skills-grid">
          {skillCategories.map((cat) => (
            <div className="skill-category" key={cat.label}>
              <div className="skill-category-label">{cat.label}</div>
              <div className="skill-tags">
                {cat.skills.map((skill) => (
                  <span className="skill-tag" key={skill}>
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Education */}
      <Section id="education">
        <span className="section-label">04 — Education</span>
        <h2 className="section-title">Background</h2>
        <div className="education-content">
          <span className="education-year">May 2021</span>
          <div>
            <h3 className="education-school">Colgate University</h3>
            <p className="education-location">Hamilton, NY</p>
            <p className="education-degree">
              B.A. double major in Political Science and Computer Science
            </p>
            <p className="education-details">
              Seminars in Human-Computer Interaction and Applied Machine Learning
              <br />
              Seminars in Constitutional Law, Administrative Law and American
              Political Theory
            </p>
          </div>
        </div>
      </Section>

      {/* Contact */}
      <Section id="contact" className="contact">
        <h2 className="contact-heading">Let's connect</h2>
        <p className="contact-text">
          Always open to interesting conversations and new opportunities.
        </p>
        <div className="contact-links">
          <a href="mailto:bckrben7@gmail.com" className="contact-link">
            bckrben7@gmail.com
          </a>
          <a href="tel:610-715-7578" className="contact-link">
            610-715-7578
          </a>
          <a
            href="https://github.com/benjaminbecker7"
            className="contact-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            GitHub
          </a>
        </div>
      </Section>

      <footer className="footer">
        <p className="footer-text">Benjamin Becker &copy; 2026</p>
      </footer>
    </main>
  );
};

export { Home };
